/**
 * Simple Angular controller that is attached to the GridOpenOrders within the index.aspx. The purpose of this is to allow the user to easily
 * fulfill open orders. Fulfilling pickups means that the original receipt was an order which requires that the users create pickup receipts
 * to state that those items have been picked up. This is important for a lot of our users because users will forget to fulfill open orders and
 * then the items will be left in the system as open orders. Eventually users have so many open orders that they ask devs to fulfill them all.
 */
/* @ngInject */ export function bulkPickupsController(
  $scope,
  Alert,
  BooksSvc,
  BusyScreen,
  uiGridConstants
) {
  const alertTitle = 'Process Open Orders';

  const COLUMNS = [
    { field: 'receiptNumber', displayName: 'Receipt #' },
    { field: 'entityName', displayName: 'Customer', cellTooltip: true },
    { field: 'itemName', displayName: 'Product', cellTooltip: true },
    { field: 'itemBarcode', cellTooltip: true },
    { field: 'qtyOrdered' },
    { field: 'qtyDelivered' },
    { field: 'qtyRemaining' }
  ];

  const gridOptions = {
    data: 'dbOpenOrders',
    enableColumnMenus: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableFiltering: true,
    enableRowSelection: true,
    enableSelectAll: true,
    minRowsToShow: 3,
    multiSelect: true,
    enableFullRowSelection: true,
    enableColumnResizing: true,
    showGridFooter: true,
    /**
     * We register the grid api to the $scope so we have reference to which rows are clicked and that way we can interact with them in this file.
     */
    onRegisterApi: function (gridApi) {
      $scope.gridApi = gridApi;

      /**
       * Due to bulkPickups being a very resource intensive process on the server, we are attempting limiting the pickups to 300 as a trial.
       *
       * Ticket: https://graystep.atlassian.net/browse/GPP-226
       */
      gridApi.selection.on.rowSelectionChanged($scope, function (row) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifySingleSelect(selectedRows.length, row, alertTitle, 300);
      });
      gridApi.selection.on.rowSelectionChangedBatch($scope, function (rows) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifyMultiSelect(selectedRows.length, rows, alertTitle, 300);
      });
    },
    columnDefs: COLUMNS
  };

  /**
   * Bind the grip options we set to the $scope so we can use them in the index file.
   */
  $scope.openOrdersOptions = gridOptions;

  /**
   * Finds all the selected rows in the grid and sets the qtyFill to the qtyRemaining. This is used to pass to the server to fulfill the open orders. This
   * fully fulfills the open orders. If you want to partially fulfill an open order then you will need to create a new pickup receipt within write new
   * receipt.
   *
   * Technically since this just sets qtyFill to qtyRemaining orders that have a negative qtyRemaining, will be corrected by this routine because it will
   * create a pickup receipt with a negative quantity.
   *
   * @returns void
   */
  function fulfillOpenOrders() {
    const selectedRows = $scope.gridApi.selection.getSelectedRows();

    if (!selectedRows.length) {
      Alert.info(
        'No open orders have been selected to fulfill. Please select one or more open orders to fulfill.',
        alertTitle
      );
      return;
    }

    /**
     * The qtyFill value is what tells the processOpenOrders method how many items to fulfill. Since we are fulfilling all of the selected items we need to
     * set the qtyFill to the qtyRemaining so that all items are fulfilled.
     */
    const openOrdersToFulfill = selectedRows.map((openOrder) =>
      Object.assign(openOrder, { qtyFill: openOrder.qtyRemaining })
    );

    BusyScreen.setBusy(true);

    BooksSvc.processOpenOrders(openOrdersToFulfill)
      // Passing undefined for both so it will get all for the organization.
      .then(() => BooksSvc.viewOpenOrders())
      .then((resp) => {
        // Refresh the vendors now that vendors have been deactivated
        $scope.dbOpenOrders = resp.data.sortOn('receiptNumber');
        BusyScreen.setBusy(false);
        Alert.success(
          'The selected open order(s) have had pickup receipts created.',
          alertTitle
        );
      })
      .catch((error) => {
        BusyScreen.setBusy(false);

        const errorMesssage =
          error.parsedResponse && error.parsedResponse.message()
            ? error.parsedResponse.message()
            : 'There was an error fulfilling those open order(s). Please try again.';

        Alert.error(errorMesssage, alertTitle);
      });
  }

  /**
   * Simple function that checks to see if there are any selected rows. If there are then we can save, if not then we cannot save.
   * If there are no selected rows then the user has not selected anything to save.
   *
   * @returns boolean (true = can Save; false = cannot save)
   */
  function canSaveOpenOrders() {
    return $scope.gridApi.selection.getSelectedRows().length > 0;
  }

  /**
   * Attach these to the $scope so they can be used in the index file.
   */
  $scope.fulfillOpenOrders = fulfillOpenOrders;
  $scope.canSaveOpenOrders = canSaveOpenOrders;
}
